import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { connect } from 'react-redux';
import { getDocumentHelper } from '../../../../actions/utility-actions';
import { DataSplitView } from '../../../../components/data-splitview/DataSplitView';
import Icon from '../../../../components/icons/Icon';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { Table } from '../../../../components/tables/Table';
import { subscribeToRouteChange } from '../../../../hocs/PageWrapper';
import { IDocumentRequest } from '../../../../models/utilities/document-request';
import { PagedApiResult } from '../../../../models/utilities/paged-request';
import { MailJobTypes } from '../../../../models/utilities/type-models';
import { StatusCode } from '../../../../utils/api';
import { _format } from '../../../../utils/date-utils';
import { MailJobRow } from '../components/MailJobRow';
import { MailJobDocument } from '../../models/mailjobs/mailjob-document';
import { MailJobRead } from '../../models/mailjobs/mailjob-read';
import { MailJobSearchParams } from '../../models/mailjobs/mailjob-search-params';
import { MailJobSummary } from '../../models/mailjobs/mailjob-summary';
import { CommunicationSvc } from '../../../../communications/service';
import '../../../../communications/styles.scss';

// #region Component State & Props

const defaultState = {
    selectedTabIdx          : 0,
    forceCreateInProgress   : false,
    summary                 : {} as MailJobSummary,
    openJobs                : {} as PagedApiResult<MailJobRead>,
    openJobSearchParams     : new MailJobSearchParams('open'),
    printedJobs             : {} as PagedApiResult<MailJobRead>,
    printedJobSearchParams  : new MailJobSearchParams('printed')
};

type State = Readonly<typeof defaultState>;

interface IProps {
    getDocumentHelper : any;
}

// #endregion

class MailTriageSceneComponent extends React.Component<IProps, State> {

    public communicationSvc : CommunicationSvc = new CommunicationSvc();
    public state : State = defaultState;

    public async componentDidMount() {
        await this.getMailJobPageData();    
    }

    public render() {

        return <PageContent pageName="[ACCOUNTING] Mail Jobs">
                    <PageHeading
                        title=""
                        useTabs={true}
                        tabLabels={[ this.getOpenLabel() , this.getPrintedLabel()]}
                        onTabChange={ this.onTabChange }
                        selectedTab={ this.state.selectedTabIdx } />
                    <PageBody>
                        {
                            this.state.selectedTabIdx === 0 ? 
                            <React.Fragment>
                                <DataSplitView
                                    actionClick={ this.onForceCreateJob }
                                    actionText="Process Pending Documents"
                                    actionDisabled={ 
                                        this.state.summary.pendingPageCount === 0 || this.state.forceCreateInProgress 
                                    }
                                    title="Summary"
                                    subtitle="Open Mail Jobs"
                                    requiredPermissions={[
                                        Permissions.CommunicationsMailTriageProcessPending
                                    ]}
                                    icon={<Icon width="60%" name="envelope" />}>
                                        <span>  
                                            Pending Document Count:
                                             <strong>
                                                { this.state.summary.pendingDocumentCount }
                                            </strong>
                                        </span>
                                        <span>
                                            Pending Page Count: 
                                            <strong>
                                                { this.state.summary.pendingPageCount }
                                            </strong>
                                        </span>
                                </DataSplitView>
                                <Table 
                                    header={[
                                        'Created On',
                                        'Document Count',
                                        'Page Count',
                                        'Status',
                                        'Actions'
                                    ]}
                                    numberOfActions={2}
                                    nextClick={ () => this.onNextClick('open') }
                                    previousClick={ () => this.onPreviousClick('open') }
                                    pageData={ this.state.openJobs.pagination }
                                    noResultsMessage="No mailjobs available">
                                    {
                                        this.state.openJobs.results
                                        ? this.state.openJobs.results.map((job : MailJobRead) => 
                                            <MailJobRow
                                                key={ job.id }
                                                mailjob={ job } 
                                                downloadAction={ this.downloadMailJob }
                                                markDoneAction={ this.markJobAsDone }
                                            />
                                        )
                                        : null
                                    }
                                </Table>
                            </React.Fragment> : null                            
                        }
                        {
                            this.state.selectedTabIdx === 1 ?                            
                                <Table 
                                    header={[
                                        'Created On',
                                        'Document Count',
                                        'Page Count',
                                        'Status',
                                        'Actions'
                                    ]}
                                    nextClick={ () => this.onNextClick('printed') }
                                    previousClick={ () => this.onPreviousClick('printed') }
                                    pageData={ this.state.printedJobs.pagination }
                                    noResultsMessage="No mailjobs available">
                                    {
                                        this.state.printedJobs.results
                                        ? this.state.printedJobs.results.map((job : MailJobRead) => 
                                            <MailJobRow
                                                key={ job.id }
                                                mailjob={ job } 
                                                downloadAction={ this.downloadMailJob }
                                                markDoneAction={ undefined }
                                            />
                                        )
                                        : null
                                    }
                                </Table> : null
                        }
                    </PageBody>
            </PageContent>;
    }

    private onForceCreateJob = async () => {
        const result = await this.communicationSvc.forceCreateMailjob();

        // If it succeeds, we wait 15 seconds and requery to give it time.
        // meh.
        if (result === StatusCode.Success) {

            this.setState({
                forceCreateInProgress: true
            });

            setTimeout(async () => {
                await this.getMailJobPageData();

                this.setState({
                    forceCreateInProgress: false
                });
            }, 15000);
        }
    }

    private getMailJobPageData = async () => {
        const result = await this.communicationSvc.getAllMailjobs(
            this.state.openJobSearchParams, 
            this.state.printedJobSearchParams
        );
        
        this.setState({
            openJobs    : result.open,
            printedJobs : result.printed,
            summary     : result.summary
        });
    }

    private onTabChange = (e: any, val: any) => {
        this.setState({
            selectedTabIdx: val
        });
    }

    private downloadMailJob = async (id: number) => {
        const req = new MailJobDocument(id);
        this.props.getDocumentHelper(req);
    }

    private markJobAsDone = async (mailJobId : number) => {        
        const result = await this.communicationSvc.printMailJob(mailJobId);
        if (result === StatusCode.Success) {
            await this.getMailJobPageData();
        }
    }

    private onPreviousClick = async (type: MailJobTypes) => {
        if (type === 'open') {
            await this.prevPageRequest(this.state.openJobSearchParams, 'open'); 
        }
        else if (type === 'printed') {
            await this.prevPageRequest(this.state.printedJobSearchParams, 'printed'); 
        }
    }

    private onNextClick = async (type: MailJobTypes) => {
        if (type === 'open') {
            await this.nextPageRequest(this.state.openJobSearchParams, 'open');            
        }
        else if (type === 'printed') {
            await this.nextPageRequest(this.state.printedJobSearchParams, 'printed');            
        }
    }

    private prevPageRequest = async (params: MailJobSearchParams, type: MailJobTypes) => {
        
        params.paginationInfo.goBack();

        const result = await this.communicationSvc.getMailJobs(params);
        if (result) {
            if (type === 'open') {
                this.setState({
                    openJobSearchParams: params,
                    openJobs: result
                });
            }
            else if (type === 'printed') {
                this.setState({
                    printedJobSearchParams: params,
                    printedJobs: result
                });
            }
        }
    }

    /**
     * Helper method to handle the next page click.  In the event that we get 
     * no results back from the request for our next, that means that we hit 
     * our last result set was everything, AND we had enough to set a full
     * page of data in the list.  
     * 
     * So in that case, we'll call goBack so we show the user some data, and
     * we'll disabled the next button.
     * 
     */
    private nextPageRequest = async (params : MailJobSearchParams, type: MailJobTypes) => {

        params.paginationInfo.goForward();

        const result = await this.communicationSvc.getMailJobs(params);

        // We got results, so set them
        if (result && result.results.length > 0) {

            if (type === 'open') {
                this.setState({
                    openJobSearchParams: params,
                    openJobs: result
                });
            }
            else if (type === 'printed') {
                this.setState({
                    printedJobSearchParams: params,
                    printedJobs: result
                });
            }

        }
        // We got no results, so replay the previous request
        else if (result && result.results.length === 0) {

            params.paginationInfo.goBack();
            const updatedResult = await this.communicationSvc.getMailJobs(params);

            if (updatedResult) {
                updatedResult.pagination.isEnd = true;

                if (type === 'open') {
                    this.setState({
                        openJobSearchParams: params,
                        openJobs: updatedResult
                    });
                }
                else if (type === 'printed') {
                    this.setState({
                        printedJobSearchParams: params,
                        printedJobs: updatedResult
                    });
                }
            }
        }
    }

    private getOpenLabel = () => {        
        if (this.state.summary.openJobs) {
            return `Open Jobs (${this.state.summary.openJobs})`;
        }
        return 'Open Jobs';
    }

    private getPrintedLabel = () => {
        if (this.state.summary.printedJobs && this.state.printedJobs.results.length > 0) {
            return `Printed Jobs (${this.state.summary.printedJobs})`;
        }
        return 'Printed Jobs';
    }

}

const mapDispatchToProps = (dispatch: any) => {
    return {
        getDocumentHelper: (request : IDocumentRequest) => dispatch(getDocumentHelper(request))
    };
};

const MailTriageScene = connect(
    null,
    mapDispatchToProps
  )(subscribeToRouteChange(MailTriageSceneComponent));
  
export { MailTriageScene };
